import React, { useEffect, useState } from 'react';
import { ReMixChart } from '../../components/recharts/ReMixCharts';
import { BrushStart, ChartType } from '../../models/chart_model';
import { COLOR_CODES } from '../../common';
import { FilterTab } from '../../components/Filters';
import { ChartTable } from '../../components/table/SummaryTable';
import { renderQuarterTick } from '../../components/recharts/components';
import { PriceAnalysisData } from '../../models/priceAnalysis';
import { getPriceAnalysisData } from '../../Rest_api/RestApi';

const views = ['Chart', 'Table'];

const INTERVAL_OPTIONS = [
  { key: 'slot', label: 'Slot' },
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' }
];

const PRICE_TYPE_OPTIONS = [
  { key: 'simple_avg', label: 'Simple Avg.' },
  { key: 'wt_avg', label: 'Wt. Avg.' }
];

export function PriceAnalysisChart({ startDate, endDate, isFullscreen = false }: { startDate: string, endDate: string, isFullscreen?: boolean }) {
  const [priceData, setPriceData] = useState<PriceAnalysisData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedView, setSelectedView] = useState(views[0]);
  const [selectedInterval, setSelectedInterval] = useState(INTERVAL_OPTIONS[1]); // Default to 'day'
  const [selectedPriceType, setSelectedPriceType] = useState(PRICE_TYPE_OPTIONS[1]); // Default to 'wt_avg'

  useEffect(() => {
    fetchPriceData();
  }, [startDate, endDate, selectedInterval, selectedPriceType]);

  const fetchPriceData = async () => {
    setIsLoading(true);
    try {
      const data = await getPriceAnalysisData({
        interval: selectedInterval.key as any,
        price_type: selectedPriceType.key as any,
        exchange_name: 'IEX',
        product_name: 'DAM',
        start_date: startDate,
        end_date: endDate
      });
      setPriceData(data);
    } catch (error) {
      console.error('Error fetching price analysis data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getXAxisKey = () => {
    switch (selectedInterval.key) {
      case 'slot':
        return 'x_axis';
      case 'day':
        return 'x_axis';
      case 'week':
        return 'x_axis';
      case 'month':
        return 'x_axis';
      default:
        return 'x_axis';
    }
  };

  const getSecondXAxisKey = () => {
    return (selectedInterval.key === 'slot' || selectedInterval.key === 'week') ? 'second_x_axis' : undefined;
  };

  const getYAxisLabel = () => {
    return 'Rs/MWh';
  };

  const getChartType = () => {
    return selectedInterval.key === 'month' ? ChartType.Bar : ChartType.Line;
  }

  const getLegends = () => {
    const legends = [
      {
        name: 'Current',
        dataKey: 'price',
        stroke: COLOR_CODES.primary,
        unit: 'Rs/MWh',
        type: getChartType()
      },
      // {
      //   name: 'Forecasted',
      //   dataKey: 'gna_forecasted_price',
      //   stroke: 'green',
      //   unit: 'Rs/MWh',
      //   type: ChartType.Line
      // }
    ];

    // Only add Prev Year price if we have data for it
    const hasPrevYearData = priceData.some(item => item.prev_year_price !== null && item.prev_year_price !== undefined);
    if (hasPrevYearData) {
      legends.push({
        name: 'Prev Year',
        dataKey: 'prev_year_price',
        stroke: '#7CBF00',
        unit: 'Rs/MWh',
        type: getChartType()
      });
    }

    // Only add rolling average if we have data for it
    const hasRollingData = priceData.some(item => item.rolling_actual_30_day_avgs !== null && item.rolling_actual_30_day_avgs !== undefined);
    if (hasRollingData) {
      legends.push({
        name: '30-Day Roll. Avg.',
        dataKey: 'rolling_actual_30_day_avgs',
        stroke: '#213E77',
        unit: 'Rs/MWh',
        type: getChartType()
      });
      legends.push({
        name: 'Prev Year 30-Day Roll. Avg.',
        dataKey: 'prev_year_rolling_actual_30_day_avgs',
        stroke: '#639FCB',
        unit: 'Rs/MWh',
        type: getChartType()
      });
    }

    // Only add GNA forecasted price if we have data for it
    const hasGnaForecastData = priceData.some(item => item.gna_forecasted_price !== null && item.gna_forecasted_price !== undefined);
    if (hasGnaForecastData) {
      legends.push({
        name: 'GNA Forecast.',
        dataKey: 'gna_forecasted_price',
        stroke: '#F15CE2',
        unit: 'Rs/MWh',
        type: ChartType.Line
      });
    }

    return legends;
  };



  return (
    <div className="text-left h-full w-full flex flex-col">
      <div className={`flex flex-row gap-x-2 ${isFullscreen ? 'justify-end' : 'justify-between'}`}>
        {!isFullscreen && (
          <h3 className="text-xl font-semibold text-gray-800 flex-shrink-0 chartHeading">
            Price Analysis
          </h3>
        )}
        <div className='flex flex-row gap-x-2 pb-2'>
          <FilterTab
            options={PRICE_TYPE_OPTIONS.map(option => option.label)}
            selected={[selectedPriceType.label]}
            isSmall={!isFullscreen}
            onClick={(label) => {
              const option = PRICE_TYPE_OPTIONS.find(opt => opt.label === label);
              if (option) setSelectedPriceType(option);
            }}
          />
          <FilterTab
            options={INTERVAL_OPTIONS.map(option => option.label)}
            selected={[selectedInterval.label]}
            isSmall={!isFullscreen}
            onClick={(label) => {
              const option = INTERVAL_OPTIONS.find(opt => opt.label === label);
              if (option) setSelectedInterval(option);
            }}
          />
          <FilterTab
            isSmall={!isFullscreen}
            options={views}
            selected={[selectedView]}
            onClick={(view) => setSelectedView(view)}
          />
          {!isFullscreen && (
            <button
              onClick={() => {
                // This will be handled by the parent component
                const event = new CustomEvent('openFullscreen', { detail: 'card1' });
                window.dispatchEvent(event);
              }}
              className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100 ml-2"
              title="Open in fullscreen"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="h-full w-full flex flex-col">
        {selectedView === 'Chart' && (
          <div className='w-full h-full flex flex-col pb-8'>
          <ReMixChart
            data={priceData as any}
            xDataKey={getXAxisKey()}
            isTimeSlot={selectedInterval.key === 'slot'}
            secondXDataKey={getSecondXAxisKey()}
            yAxisLabel={getYAxisLabel()}
            secondYAxisLabel='Rs/MWh'
            fontSize={10}
            
            // chartContainerHeight='88%'
            // yAxisWidth={50}
            unit='Rs/MWh'
            rendexSecondXTick={renderQuarterTick as any}
            onlyTitle={selectedInterval.key !== 'slot' && selectedInterval.key !== 'week'}
            brushStart={BrushStart.End}
            showBrush={true}
            yMaxValue={10000}
            brushIndex={{
              startIndex: Math.max(0, priceData.length - 96),
              endIndex: priceData.length
            }}
            syncid={'price-analysis-' + selectedInterval.label}
            isSmallLegend={true}
            legends={getLegends()}
            customLegendOrder={['Prev Year', 'Current', 'GNA Forecast.', 'Prev Year 30-Day Roll. Avg.', '30-Day Roll. Avg.']}
          /></div>
        )}
        {selectedView === 'Table' && (
          <ChartTable
            data={priceData}
            legends={getLegends()}
            unit="Rs/MWh"
            extraKeys={(selectedInterval.key === 'slot' || selectedInterval.key === 'week') ? ['x_axis', 'second_x_axis'] : ['x_axis']}
            extraLabels={(selectedInterval.key === 'slot' || selectedInterval.key === 'week') ? ['Time Slot', 'Date'] : ['Date']}
            isSmall={true}
          />
        )}
        
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center z-10">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
              <p className="text-gray-700 text-sm font-medium"></p>
            </div>
          </div>
        )}

      </div>
      
      {/* Price Type Filter Tabs Below Chart */}

    </div>
  );
}
